import { useStandardButtonStyles } from "../../../styles/standardButtons";
import { toast } from "react-toastify";
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, Typography, Box, Grid,
  Switch
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import { i18n } from "../../../translate/i18n";

// [Plan 904] Modal de edición: Lista Interactiva META API
// standard button hook
const FlowBuilderInteractiveListModal = ({ node, onClose, onSave }) => {
  const standardClasses = useStandardButtonStyles();
  const [name, setName] = useState("");
  const [header, setHeader] = useState("");
  const [body, setBody] = useState("");
  const [buttonText, setButtonText] = useState("Ver opciones");
  const [antiBan, setAntiBan] = useState(false);
  const [rows, setRows] = useState([
    { id: "row_1", title: "", description: "", varName: "", varValue: "" },
    { id: "row_2", title: "", description: "", varName: "", varValue: "" }
  ]);

  useEffect(() => {
    if (node && node.data) {
      setName(node.data.name || "");
      setHeader(node.data.header || "");
      setBody(node.data.body || "");
      setButtonText(node.data.buttonText || "Ver opciones");
      setAntiBan(!!node.data.antiBan);
      setRows(node.data.rows && node.data.rows.length > 0
        ? node.data.rows
        : [{ id: "row_1", title: "", description: "", varName: "", varValue: "" }]);
    }
  }, [node]);

  const handleSave = () => {
    // Normalizar IDs de filas para consistencia con el backend
    const normalizedRows = rows.map((row, idx) => ({
      ...row,
      id: row.id || `row_${idx + 1}`
    }));
    onSave({ name, header, body, buttonText, rows: normalizedRows, antiBan });
  };

  const addRow = () => {
    if (rows.length >= 10) return;
    const newId = `row_${rows.length + 1}`;
    setRows(prev => [...prev, { id: newId, title: "", description: "", varName: "", varValue: "" }]);
  };

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    const maxLen = field === "title" ? 24 : 72;
    setRows(prev => prev.map((row, i) =>
      i === idx ? { ...row, [field]: value.substring(0, maxLen) } : row
    ));
  };

  const updateRowVar = (idx, field, value) => {
    setRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <span style={{ fontSize: 18 }}>🔒</span>
          <Typography variant="h6" color="primary" style={{ fontWeight: 700 }}>
            {i18n.t("flowBuilder.interactiveList.editTitle")}
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("flowBuilder.interactiveList.subtitle")}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Nombre del nodo */}
        <TextField
          label={i18n.t("flowBuilder.interactiveList.nodeName")}
          value={name}
          onChange={e => setName(e.target.value)}
          variant="outlined"
          margin="dense"
          fullWidth
          style={{ marginBottom: 12 }}
        />

        {/* Header */}
        <TextField
          label={i18n.t("flowBuilder.interactiveList.header")}
          value={header}
          onChange={e => setHeader(e.target.value.substring(0, 60))}
          variant="outlined"
          margin="dense"
          fullWidth
          inputProps={{ maxLength: 60 }}
          helperText={`${header.length}/60`}
          style={{ marginBottom: 8 }}
        />

        {/* Cuerpo */}
        <TextField
          label={i18n.t("flowBuilder.interactiveList.messageBody")}
          value={body}
          onChange={e => setBody(e.target.value)}
          variant="outlined"
          margin="dense"
          fullWidth
          multiline
          rows={3}
          style={{ marginBottom: 8 }}
        />

        {/* Texto del botón de lista */}
        <TextField
          label={i18n.t("flowBuilder.interactiveList.buttonText")}
          value={buttonText}
          onChange={e => setButtonText(e.target.value.substring(0, 20))}
          variant="outlined"
          margin="dense"
          fullWidth
          inputProps={{ maxLength: 20 }}
          helperText={`${buttonText.length}/20`}
          style={{ marginBottom: 16 }}
        />

        {/* Opciones de la Lista */}
        <Typography variant="subtitle2" color="primary" style={{ fontWeight: 700, marginBottom: 4 }}>
          {i18n.t("flowBuilder.interactiveList.listOptions")} ({rows.length}/10)
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
          {i18n.t("flowBuilder.interactiveList.listOptionsDesc")}
        </Typography>

        {rows.map((row, idx) => (
          <Box key={idx} style={{ marginBottom: 10 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={5}>
                <TextField
                  label={i18n.t("flowBuilder.interactiveList.optionLabel", { index: idx + 1 })}
                  value={row.title}
                  onChange={e => updateRow(idx, "title", e.target.value)}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  inputProps={{ maxLength: 24 }}
                  helperText={`Handle: row-${row.id || `row_${idx + 1}`}`}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={i18n.t("flowBuilder.interactiveList.descriptionLabel")}
                  value={row.description}
                  onChange={e => updateRow(idx, "description", e.target.value)}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  inputProps={{ maxLength: 72 }}
                  helperText=" "
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton
                  size="small"
                  onClick={() => removeRow(idx)}
                  color="secondary"
                  disabled={rows.length <= 1}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
            
            <Box style={{ display: "flex", gap: 8, marginTop: 4, paddingLeft: 8, paddingRight: 8 }}>
              <TextField
                label={i18n.t("flowBuilder.interactiveButtons.varName", "Variable (opcional)")}
                value={row.varName || ""}
                onChange={e => updateRowVar(idx, "varName", e.target.value)}
                variant="outlined"
                margin="dense"
                size="small"
                style={{ flex: 1 }}
                inputProps={{ style: { fontSize: '0.8rem' } }}
                helperText={i18n.t("flowBuilder.interactiveButtons.varNameHelper", "Nombre variable")}
              />
              <TextField
                label={i18n.t("flowBuilder.interactiveButtons.varValue", "Valor a guardar")}
                value={row.varValue || ""}
                onChange={e => updateRowVar(idx, "varValue", e.target.value)}
                variant="outlined"
                margin="dense"
                size="small"
                style={{ flex: 2 }}
                inputProps={{ style: { fontSize: '0.8rem' } }}
                helperText={i18n.t("flowBuilder.interactiveButtons.varValueHelper", "Se guarda al seleccionar")}
              />
            </Box>
          </Box>
        ))}

        {rows.length < 10 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addRow}
            color="primary"
            style={{ marginTop: 4 }}
          >
            {i18n.t("flowBuilder.interactiveList.addOption")}
          </Button>
        )}
      {/* AntiBan Toggle */}
      <Box style={{
        marginTop: "20px",
        marginBottom: "8px",
        padding: "12px 16px",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        borderRadius: "8px",
        background: antiBan ? "rgba(74,222,128,0.07)" : "rgba(0,0,0,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Box>
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: antiBan ? "#4ade80" : "rgba(255,255,255,0.7)" }}>
            🛡️ AntiBan IA {antiBan ? "[Activado]" : "[Desactivado]"}
          </Typography>
          <Typography variant="caption" style={{ color: "rgba(255,255,255,0.5)" }}>
            {antiBan
              ? "La IA reescribirá el mensaje automáticamente para evitar detección de spam."
              : "El mensaje se enviará exactamente como fue escrito (asume el riesgo)."}
          </Typography>
        </Box>
        <Switch
          checked={antiBan}
          onChange={(e) => setAntiBan(e.target.checked)}
          color="primary"
          style={{ color: antiBan ? "#4ade80" : undefined }}
        />
      </Box>

      </DialogContent>
      <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid rgba(255, 255, 255, 0.12)" }}>
        <Button onClick={onClose} className={standardClasses.secondaryButton}>
          {i18n.t("flowBuilder.interactiveList.cancel")}
        </Button>
        <Button onClick={handleSave} className={standardClasses.applyButton}>
          {i18n.t("flowBuilder.interactiveList.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderInteractiveListModal;
